class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    window.requestAnimationFrame(() => window.CartDrawerSavingsRefresh && window.CartDrawerSavingsRefresh());
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      window.CartDrawerSavingsRefresh && window.CartDrawerSavingsRefresh();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

(function () {
  let refreshTimer = null;
  let applying = false;

  function getCartCurrency(cart) {
    return (
      cart?.currency ||
      window.Shopify?.currency?.active ||
      window.Shopify?.currency?.shopCurrency ||
      document.documentElement.getAttribute('data-currency') ||
      'AUD'
    );
  }

  function formatMoney(cents, stripCents, currency) {
    const activeCurrency = currency || window.Shopify?.currency?.active || 'AUD';

    let formatted = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: activeCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(cents || 0) / 100);

    if (stripCents) {
      formatted = formatted.replace(/\.00\b/, '').replace(/,00\b/, '');
    }

    return formatted;
  }

  function ensureStyles() {
    const existing = document.getElementById('cd-cart-savings-styles');
    if (existing) existing.remove();

    const style = document.createElement('style');
    style.id = 'cd-cart-savings-styles';
    style.textContent = `
      #CartDrawer,
      #CartDrawer .drawer__inner,
      #CartDrawer .cart-drawer__body,
      #CartDrawer .drawer__contents,
      #CartDrawer .drawer__cart-items-wrapper,
      #CartDrawer cart-drawer-items {
        overflow-x: hidden !important;
        max-width: 100% !important;
      }
      #CartDrawer cart-drawer-items::-webkit-scrollbar:horizontal,
      #CartDrawer .drawer__contents::-webkit-scrollbar:horizontal,
      #CartDrawer .drawer__cart-items-wrapper::-webkit-scrollbar:horizontal {
        height: 0 !important;
        display: none !important;
      }
      #CartDrawer .drawer__header { padding: 18px 0 14px; border-bottom: 1px solid #e7e7e7; }
      #CartDrawer .drawer__heading { margin: 0; font-size: 2.8rem; line-height: 1.1; letter-spacing: 0; }
      #CartDrawer .cart-items thead { display: none !important; }
      #CartDrawer .drawer__cart-items-wrapper { padding-top: 10px; }
      #CartDrawer .cart-items,
      #CartDrawer .cart-items tbody,
      #CartDrawer .cart-items ul,
      #CartDrawer .cart-items.list-unstyled { width: 100% !important; max-width: 100% !important; overflow: hidden !important; }
      #CartDrawer .cart-item {
        position: relative !important;
        display: grid !important;
        grid-template-columns: 88px minmax(0, 1fr) 136px;
        grid-template-rows: auto auto;
        column-gap: 12px;
        row-gap: 8px;
        align-items: start;
        width: 100% !important;
        max-width: 100% !important;
        padding: 10px 0 14px;
        margin: 0 !important;
        overflow: hidden !important;
      }
      #CartDrawer .cart-item + .cart-item { margin-top: 8px !important; }
      #CartDrawer .cart-item > td { padding: 0 !important; }
      #CartDrawer .cart-item__media { grid-column: 1; grid-row: 1 / 3; width: 88px; max-width: 88px; }
      #CartDrawer .cart-item__image { width: 88px; max-width: 88px; height: auto; display: block; }
      #CartDrawer .cart-item__details { grid-column: 2; grid-row: 1; width: auto; min-width: 0; padding-right: 4px !important; }
      #CartDrawer .cart-item__name { font-size: 1.8rem; line-height: 1.08; font-weight: 500; color: #111; margin: 0 0 6px; max-width: 100%; }
      #CartDrawer .cart-item__totals { grid-column: 3; grid-row: 1; width: 136px; min-width: 136px; max-width: 136px; justify-content: flex-end; align-self: start; padding-right: 24px !important; }
      #CartDrawer .cart-item__price-wrapper { display: flex; flex-direction: column; align-items: flex-end; text-align: right; width: 100%; }
      #CartDrawer .cd-line-savings-prices { display: flex !important; flex-direction: row !important; align-items: baseline; justify-content: flex-end; gap: 6px; flex-wrap: nowrap !important; white-space: nowrap; width: 100%; }
      #CartDrawer .cd-line-savings-prices .cart-item__old-price {
        color: #8b8b8b !important;
        background: transparent !important;
        font-size: 1.08rem !important;
        font-weight: 400 !important;
        opacity: 1 !important;
        text-decoration-thickness: 1px;
        line-height: 1;
      }
      #CartDrawer .cd-line-savings-prices .price { color: #0B363C; background: transparent !important; font-size: 1.55rem; font-weight: 800; line-height: 1; }
      #CartDrawer .cd-line-saving-label { display: block; margin-top: 4px; color: #000; font-size: 1.05rem; line-height: 1; font-weight: 800; white-space: nowrap; }
      #CartDrawer .cd-per-unit-label { display: block; margin-top: 3px; color: #555; font-size: 0.95rem; line-height: 1; font-weight: 600; white-space: nowrap; }
      #CartDrawer .cart-item__details > .product-option:not(.cd-detail-price-row),
      #CartDrawer .cart-item__details > .cart-item__discounted-prices:not(.cd-detail-price-row) > span { display: none !important; }
      #CartDrawer .cart-item__details > .cart-item__discounted-prices:not(.cd-detail-price-row) { margin: 0 !important; }
      #CartDrawer .cd-detail-price-row { display: flex !important; align-items: center; flex-wrap: nowrap; gap: 5px; margin: 3px 0 0; font-size: 1.4rem; line-height: 1.1; white-space: nowrap; }
      #CartDrawer .cd-detail-price-row s { color: #666; text-decoration-thickness: 1px; font-weight: 400; }
      #CartDrawer .cd-detail-price-row strong { color: #0B363C; font-weight: 800; }
      #CartDrawer .discounts,
      #CartDrawer .cart-drawer__discounts { margin-top: 5px; margin-bottom: 0; }
      #CartDrawer .discounts__discount,
      #CartDrawer .cart-drawer__discounts .badge { font-size: 1.15rem; line-height: 1.15; padding: 3px 8px; border-radius: 999px; background: #f4f4f4; color: #111; display: inline-flex; align-items: center; gap: 5px; font-weight: 800; box-shadow: 0 2px 10px rgba(0,0,0,0.08); max-width: 100%; }
      #CartDrawer .discounts__discount svg, #CartDrawer .discounts__discount .svg-wrapper,
      #CartDrawer .cart-drawer__discounts .badge svg { width: 12px; height: 12px; flex: 0 0 12px; }
      #CartDrawer .cart-item__quantity { grid-column: 2; grid-row: 2; width: auto; padding: 0 !important; align-self: start; position: static !important; }
      #CartDrawer .cart-item__quantity-wrapper,
      #CartDrawer .quantity-popover-wrapper,
      #CartDrawer quantity-popover { display: flex; align-items: center; justify-content: flex-start; gap: 8px; position: static !important; width: 100%; }
      #CartDrawer .quantity-popover-container { width: auto; }
      #CartDrawer quantity-input.quantity.cart-quantity { width: 11.2rem; min-height: 3.2rem; height: 3.2rem; border: 1px solid #888; border-radius: 6px; overflow: hidden; }
      #CartDrawer quantity-input.quantity.cart-quantity:before, #CartDrawer quantity-input.quantity.cart-quantity:after { content: none; }
      #CartDrawer .quantity__button { width: 3.4rem; min-width: 3.4rem; height: 3.2rem; min-height: 3.2rem; }
      #CartDrawer .quantity__input { height: 3.2rem; min-height: 3.2rem; font-size: 1.45rem; font-weight: 700; }
      #CartDrawer .cart-item cart-remove-button,
      #CartDrawer cart-remove-button { position: absolute !important; top: 10px !important; right: 0 !important; left: auto !important; margin: 0 !important; transform: none !important; z-index: 2; }
      #CartDrawer cart-remove-button .button { min-width: 2.8rem; min-height: 2.8rem; width: 2.8rem; height: 2.8rem; padding: 0; }
      #CartDrawer cart-remove-button .icon-remove { width: 1.45rem; height: 1.45rem; }
      #CartDrawer .drawer__footer { overflow-x: hidden !important; }
      #CartDrawer .drawer__footer:before,
      #CartDrawer .drawer__footer:after,
      #CartDrawer .cart-drawer__footer:before,
      #CartDrawer .cart-drawer__footer:after { display: none !important; content: none !important; }
      #CartDrawer .cd-cart-totals { display: flex; flex-direction: column; gap: 4px; margin: 0; border-top: 1px solid #d5d5d5; padding-top: 14px; }
      #CartDrawer .cd-cart-totals__row { display: flex; justify-content: space-between; align-items: baseline; margin: 0; font-family: 'Poppins', sans-serif; line-height: 1.15; }
      #CartDrawer .cd-cart-totals__row--savings { color: #0B363C; font-size: 1.6rem; font-weight: 700; }
      #CartDrawer .cd-cart-totals__row--subtotal { color: #000; font-size: 2rem; font-weight: 800; }
      #CartDrawer #CartDrawer-Checkout { border-radius: 6px !important; min-height: 5.2rem; }
      #CartDrawer .cart-item__details .cart-item__final-price:empty,
      #CartDrawer .cart-item__details .product-option:empty { display: none; }
      @media screen and (max-width: 749px) {
        #CartDrawer .drawer__heading { font-size: 2.6rem; }
        #CartDrawer .cart-item { grid-template-columns: 82px minmax(0, 1fr) 124px; column-gap: 10px; }
        #CartDrawer .cart-item__media, #CartDrawer .cart-item__image { width: 82px; max-width: 82px; }
        #CartDrawer .cart-item__name { font-size: 1.65rem; }
        #CartDrawer .cart-item__totals { width: 124px; min-width: 124px; max-width: 124px; padding-right: 20px !important; }
        #CartDrawer .cd-line-savings-prices { gap: 4px; }
        #CartDrawer .cd-line-savings-prices .cart-item__old-price { font-size: 0.98rem !important; }
        #CartDrawer .cd-line-savings-prices .price { font-size: 1.35rem; }
        #CartDrawer .cd-line-saving-label { font-size: 0.94rem; letter-spacing: -0.01em; }
        #CartDrawer quantity-input.quantity.cart-quantity { width: 10.8rem; }
        #CartDrawer .cd-cart-totals__row--savings { font-size: 1.5rem; }
        #CartDrawer .cd-cart-totals__row--subtotal { font-size: 1.9rem; }
      }
    `;
    document.head.appendChild(style);
  }

  async function getCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Could not fetch cart');
    return response.json();
  }

  function updateHeading(cart) {
    const heading = document.querySelector('#CartDrawer .drawer__heading');
    if (!heading || !cart) return;
    const count = cart.item_count || 0;
    heading.textContent = count > 0 ? `Your cart • ${count} ${count === 1 ? 'item' : 'items'}` : 'Your cart';
  }

  function cleanFreeGiftLabels(row) {
    row.querySelectorAll('.cart-item__details .cart-item__final-price, .cart-item__details .product-option').forEach((element) => {
      if (element.textContent.trim().toLowerCase() === 'free gift') element.remove();
    });

    row.querySelectorAll('.discounts__discount, .badge').forEach((element) => {
      if (/free\s*gift/i.test(element.textContent || '')) element.remove();
    });
  }

  function itemGroupKey(item) {
    return String(item.variant_id || item.product_id || item.key || item.title || 'cart-item');
  }

  function buildItemGroups(cart) {
    const groups = {};
    (cart.items || []).forEach((item) => {
      const key = itemGroupKey(item);
      if (!groups[key]) groups[key] = { quantity: 0, finalTotal: 0, originalTotal: 0 };
      groups[key].quantity += Number(item.quantity || 0);
      groups[key].finalTotal += Number(item.final_line_price != null ? item.final_line_price : item.line_price || 0);
      groups[key].originalTotal += Number(item.original_line_price || item.line_price || 0);
    });
    return groups;
  }

  function getEffectiveUnitPrice(item, groups) {
    const group = groups[itemGroupKey(item)];
    if (!group || !group.quantity) return Number(item.final_price || item.price || item.line_price || 0);
    return Math.round(group.finalTotal / group.quantity);
  }

  function updateDetailPrice(row, item, original, final, savings, currency) {
    let detailPrice = row.querySelector('.cd-detail-price-row');
    if (!detailPrice) {
      detailPrice = document.createElement('div');
      detailPrice.className = 'cd-detail-price-row';
      const name = row.querySelector('.cart-item__name');
      if (name) name.insertAdjacentElement('afterend', detailPrice);
    }

    if (savings > 0) {
      detailPrice.innerHTML = `<s>${formatMoney(original, false, currency)}</s><strong>${formatMoney(final, false, currency)}</strong>`;
    } else {
      const unitPrice = Number(item.original_price || item.price || final || 0);
      detailPrice.innerHTML = `<span>${formatMoney(unitPrice, false, currency)}</span>`;
    }
  }

  function updateLineItems(cart) {
    const rows = document.querySelectorAll('#CartDrawer-CartItems .cart-item');
    const groups = buildItemGroups(cart);
    const currency = getCartCurrency(cart);
    rows.forEach((row, index) => {
      const item = cart.items && cart.items[index];
      if (!item) return;

      cleanFreeGiftLabels(row);

      const wrapper = row.querySelector('.cart-item__price-wrapper');
      if (!wrapper) return;

      const original = Number(item.original_line_price || item.line_price || 0);
      const final = Number(item.final_line_price != null ? item.final_line_price : item.line_price || 0);
      const savings = original - final;

      updateDetailPrice(row, item, original, final, savings, currency);

      if (savings > 0) {
        wrapper.innerHTML = `
          <div class="cart-item__discounted-prices cd-line-savings-prices">
            <span class="visually-hidden">Regular price</span>
            <s class="cart-item__old-price price price--end">${formatMoney(original, false, currency)}</s>
            <span class="visually-hidden">Sale price</span>
            <span class="price price--end">${formatMoney(final, false, currency)}</span>
          </div>
          <span class="cart-drawer-item__saving cd-line-saving-label"><strong>(You save ${formatMoney(savings, true, currency)})</strong></span>
        `;
      } else {
        const effectiveUnitPrice = getEffectiveUnitPrice(item, groups);
        wrapper.innerHTML = `
          <span class="price price--end">${formatMoney(effectiveUnitPrice, false, currency)}</span>
          <span class="cd-per-unit-label">/ bar</span>
        `;
      }
    });
  }

  function updateFooter(cart) {
    const footer = document.querySelector('#CartDrawer .drawer__footer > .cart-drawer__footer');
    if (!footer) return;

    const original = Number(cart.original_total_price || 0);
    const total = Number(cart.total_price || 0);
    const savings = Math.max(original - total, 0);
    const currency = getCartCurrency(cart);

    footer.innerHTML = `
      <div class="cart-drawer__totals cd-cart-totals" role="status">
        ${savings > 0 ? `
          <p class="cart-drawer__totals__row cart-drawer__totals__row--spaced cd-cart-totals__row cd-cart-totals__row--savings">
            <span><strong>Savings</strong></span>
            <span><strong>-${formatMoney(savings, false, currency)}</strong></span>
          </p>
        ` : ''}
        <p class="cart-drawer__totals__row cart-drawer__totals__row--spaced cd-cart-totals__row cd-cart-totals__row--subtotal">
          <span><strong>Subtotal</strong></span>
          <span><strong>${formatMoney(total, false, currency)}</strong></span>
        </p>
      </div>
    `;
  }

  function updateCheckoutCopy() {
    const checkoutButton = document.querySelector('#CartDrawer-Checkout');
    if (!checkoutButton) return;
    checkoutButton.textContent = 'Check out';
  }

  async function refreshCartDrawerSavings() {
    if (applying) return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    applying = true;
    try {
      ensureStyles();
      const cart = await getCart();
      updateHeading(cart);
      updateLineItems(cart);
      updateFooter(cart);
      updateCheckoutCopy();
    } catch (error) {
      console.error('Cart drawer savings refresh failed:', error);
    } finally {
      window.setTimeout(() => {
        applying = false;
      }, 80);
    }
  }

  function scheduleRefresh() {
    if (applying) return;
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refreshCartDrawerSavings, 120);
  }

  window.CartDrawerSavingsRefresh = scheduleRefresh;

  document.addEventListener('DOMContentLoaded', scheduleRefresh);
  document.addEventListener('shopify:section:load', scheduleRefresh);

  const observer = new MutationObserver(() => scheduleRefresh());
  observer.observe(document.documentElement, { childList: true, subtree: true });

  scheduleRefresh();
})();
